/**
 * Stationsverwaltung - Enterprise-Pattern
 * 
 * UX-Leitlinie:
 * - Keine Create/Edit-Modals
 * - Klick auf Zeile → Detailseite
 * - Delete/Deactivate nur auf Detailseite
 * - Keine Trash-Icons in Listen
 * - Create erfolgt via Pairing-Flow (/pair)
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, RefreshCw, Search, Info, ChevronRight, Link as LinkIcon } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

type StationType = 'WSA' | 'VORARBEITER' | 'ADMIN';

interface Station {
  id: string;
  name: string | null;
  type: StationType;
  location: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  registeredBy: { displayName: string } | null;
}

const TYPE_LABELS: Record<StationType, string> = {
  WSA: 'WSA-Erfassung',
  VORARBEITER: 'Vorarbeiter',
  ADMIN: 'Admin',
};

const TYPE_COLORS: Record<StationType, string> = {
  WSA: 'bg-blue-100 text-blue-800',
  VORARBEITER: 'bg-green-100 text-green-800',
  ADMIN: 'bg-orange-100 text-orange-800',
};

export default function StationsPage() {
  const navigate = useNavigate();
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Load stations
  const loadStations = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/stations', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStations(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Stationen:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStations();
  }, []);

  // Filter (Name, Standort)
  const filtered = useMemo(() => {
    if (!search) return stations;
    const s = search.toLowerCase();
    return stations.filter(st => 
      st.name?.toLowerCase().includes(s) ||
      st.location?.toLowerCase().includes(s) ||
      st.id.toLowerCase().includes(s)
    );
  }, [stations, search]);

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '–';
    return new Date(dateStr).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Monitor className="w-6 h-6" />
              Stationen
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} von {stations.length} Stationen
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/pair?returnTo=%2Fadmin%2Fstations')}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
            >
              <LinkIcon className="w-4 h-4" />
              Dieses Geraet koppeln
            </button>
            <button
              onClick={loadStations}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Neue Stationen</strong> werden via <strong>Pairing</strong> angelegt: 
            Klicken Sie auf "Dieses Geraet koppeln" oder navigieren Sie auf dem Zielgeraet zu <code className="bg-blue-100 px-1 rounded">/pair</code>.
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Suchen (Name, Standort, ID)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search ? 'Keine Treffer' : 'Keine Stationen vorhanden'}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Typ</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Standort</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Zuletzt gesehen</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(s => (
                  <tr 
                    key={s.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/stations/${s.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{s.name || <span className="text-slate-400">Unbenannt</span>}</div>
                      <div className="text-xs text-slate-400 font-mono">{s.id.slice(0, 8)}...</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${TYPE_COLORS[s.type]}`}>
                        {TYPE_LABELS[s.type]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{s.location || '–'}</td>
                    <td className="px-4 py-3 text-slate-600 text-sm hidden md:table-cell">{formatDate(s.lastSeenAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
